import React from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

interface ResubmitErrorDialogProps {
  open: boolean;
  onClose: () => void;
  error: { message?: string; error_code?: string } | null;
  encounterId?: string | null;
  claimId?: string | null;
  resubmitType: '7' | '8';
}

export const ResubmitErrorDialog: React.FC<ResubmitErrorDialogProps> = ({
  open,
  onClose,
  error,
  encounterId,
  claimId,
  resubmitType,
}) => {
  const navigate = useNavigate();
  const type = resubmitType === '7' ? 'correction' : 'void';
  const message = error?.message ?? '';
  const missingReference =
    message.includes('payer claim control number') || message.includes('frequency code 1');
  const hasLinkedRetryGuidance = message.includes('available linked retry');
  const explanation = missingReference && !hasLinkedRetryGuidance
    ? "JotBill is missing the payer's claim reference. This needs payer-state investigation: review the remittance and payer status with your billing team or support to verify the reference and payer routing before creating a correction or void."
    : message || 'An unexpected error occurred. Please try again or contact support.';

  const handleViewClaim = () => {
    if (!claimId) return;
    navigate(
      encounterId
        ? `/jotbill/encounter/${encounterId}?workspace=claim&claim_id=${claimId}`
        : `/jotbill/claim/${claimId}`,
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ErrorOutlineIcon color="error" />
        Cannot create {type}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">{explanation}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        {claimId && (
          <Button variant="contained" startIcon={<LaunchIcon />} onClick={handleViewClaim}>
            View Claim
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
